import { create } from 'zustand';
import type { User, Scan } from '../types';
import api from '../lib/api';

interface AppState {
  user: User | null;

  scans: Scan[];
  currentScan: Scan | null;
  isLoadingScans: boolean;
  scansError: string | null;

  setUser: (user: User | null) => void;
  clearIdentity: () => void;

  fetchUser: () => Promise<void>;
  fetchScans: () => Promise<void>;
  pollScan: (scanId: string, signal?: AbortSignal) => Promise<Scan>;
  setCurrentScan: (scan: Scan | null) => void;
}

let identityGeneration = 0;
export const useStore = create<AppState>(set => ({
  user: null,
  scans: [],
  currentScan: null,
  isLoadingScans: false,
  scansError: null,
  setUser: user => set({ user }),
  clearIdentity: () => {
    identityGeneration++;
    set({
      user: null,
      scans: [],
      currentScan: null,
      isLoadingScans: false,
      scansError: null,
    });
  },
  fetchUser: async () => {
    const generation = identityGeneration;
    const res = await api.get<User>('/api/me');
    if (generation === identityGeneration) set({ user: res.data });
  },
  fetchScans: async () => {
    const generation = identityGeneration;
    set({ isLoadingScans: true, scansError: null });
    try {
      const res = await api.get<{ scans: Scan[] }>('/api/scans');
      if (generation === identityGeneration)
        set({ scans: res.data.scans, scansError: null });
    } catch (error: unknown) {
      const apiError = error as {
        response?: { data?: { error?: string } };
      };
      const message =
        apiError?.response?.data?.error ?? 'Failed to load scan history';
      if (generation === identityGeneration) set({ scansError: message });
    } finally {
      if (generation === identityGeneration) set({ isLoadingScans: false });
    }
  },
  pollScan: async (scanId: string, signal?: AbortSignal): Promise<Scan> => {
    const generation = identityGeneration;
    return new Promise((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      let settled = false;

      const cleanup = () => {
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
        signal?.removeEventListener('abort', abortPolling);
      };

      const abortPolling = () => {
        if (settled) return;

        settled = true;
        cleanup();
        reject(new DOMException('Scan polling was cancelled', 'AbortError'));
      };

      const poll = async () => {
        if (signal?.aborted || settled) {
          abortPolling();
          return;
        }

        try {
          const res = await api.get<Scan>(`/api/scans/${scanId}`, {
            signal,
          });
          const scan = res.data;
          if (generation !== identityGeneration)
            throw new DOMException('Session identity changed', 'AbortError');

          if (signal?.aborted || settled) {
            abortPolling();
            return;
          }

          if (scan.status === 'done' || scan.status === 'error') {
            settled = true;
            cleanup();
            set(state => ({
              scans: state.scans.map(s => (s.id === scanId ? scan : s)),
              currentScan: scan,
            }));
            // Resolve for both done and error states
            resolve(scan);
            return;
          }

          timeoutId = setTimeout(() => {
            void poll();
          }, 2000);
        } catch (err) {
          if (signal?.aborted || settled) {
            abortPolling();
            return;
          }

          settled = true;
          cleanup();
          reject(err);
        }
      };

      signal?.addEventListener('abort', abortPolling, { once: true });

      if (signal?.aborted) {
        abortPolling();
        return;
      }

      void poll();
    });
  },
  setCurrentScan: scan => set({ currentScan: scan }),
}));
